
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import apiClient from "../../services/apiClient";
import moment from "moment";

interface AppointmentDialogProps {
    open: boolean;
    onClose: () => void;
    initialValues: any;
    onSave: (formData: any) => void;
    isEdit?: boolean;
    saving?: boolean;
}

// Validation schema
const AppointmentSchema = Yup.object().shape({
    date: Yup.string().required("Date is required"),
    vetId: Yup.string().required("Vet is required"),
    parentId: Yup.string().required("Parent is required"),
    petId: Yup.string().required("Pet is required"),
    time: Yup.string().required("Time is required"),
    status: Yup.string().optional(),
    appointmentType: Yup.string().required("Consult type is required"),
    reasonForConsult: Yup.string().required("Reason for consult is required"),
    chargeCodeId: Yup.string().required("Charge code is required"),
});

const appointmentTypes = [
    { label: "Tele Consult", value: "Tele Consult" },
    { label: "Walk In", value: "Walk In" },
    { label: "Home Visit", value: "Home Visit" },
];

const defaultInitialValues = {
    date: "",
    vetId: "",
    parentId: "",
    petId: "",
    time: "",
    status: "",
    appointmentType: "",
    chargeCodeId: "",
};

export const consultStatuses = [
    { label: "NEW", value: "NEW" },
    { label: "STARTED", value: "STARTED" },
    { label: "WAITING", value: "WAITING" },
    { label: "ENDED", value: "ENDED" },
    { label: "FAILED", value: "FAILED" },
    { label: "CANCELLED", value: "CANCELLED" },
    { label: "EXPIRED", value: "EXPIRED" },
    { label: "UNPAID", value: "UNPAID" },
    { label: "PAID", value: "PAID" },
    { label: "PENDING", value: "PENDING" },
    { label: "ACCEPTED", value: "ACCEPTED" },
    { label: "CREATED", value: "CREATED" },
    { label: "COMPLETED", value: "COMPLETED" },
    { label: "VET ACCEPTED", value: "VET_ACCEPTED" },
    { label: "PAYMENT PENDING", value: "PAYMENT_PENDING" },
    { label: "CONFIRMED", value: "CONFIRMED" },
    { label: "LINK READY", value: "LINK_READY" },
    { label: "VET JOINED", value: "VET_JOINED" },
    { label: "JOINED (YOU)", value: "JOINED_YOU" },
    { label: "INPROGRESS", value: "INPROGRESS" },
    { label: "CANCELLED (VET)", value: "CANCELLED_VET" },
    { label: "CANCELLED (YOU)", value: "CANCELLED_YOU" },
    { label: "RESCHEDULE REQUESTED", value: "RESCHEDULE_REQUESTED" },
    { label: "RESCHEDULED", value: "RESCHEDULED" },
    { label: "RESCHEDULE REJECTED", value: "RESCHEDULE_REJECTED" },
    { label: "NO SHOW (YOU)", value: "NO_SHOW_YOU" },
    { label: "NO SHOW (VET)", value: "NO_SHOW_VET" },
    { label: "REFUND PENDING", value: "REFUND_PENDING" },
    { label: "REFUNDED", value: "REFUNDED" },
    { label: "REFUND FAILED", value: "REFUND_FAILED" },
    { label: "REFUND INITIATED", value: "REFUND_INITIATED" },
];

// Standalone computeInvoice function
const computeInvoice = (chargeCodeId: string, chargeCodes: any[]) => {
    const selectedCharge = chargeCodes.find((cc) => cc.value === chargeCodeId);
    if (!selectedCharge) return null;

    const baseAmount = selectedCharge.amount || 0;
    let taxes: { name: string; rate: number; type: string; amount: number }[] = [];

    try {
        const parsedTaxes = JSON.parse(selectedCharge.tax_codes || "[]");
        taxes = parsedTaxes.map((tax: any) => {
            const taxAmount =
                tax.type === "percentage"
                    ? (baseAmount * parseFloat(tax.rate || 0)) / 100
                    : parseFloat(tax.rate || 0);
            return { ...tax, amount: taxAmount };
        });
    } catch (err) {
        console.error("Error parsing tax codes", err);
    }

    const totalAmount = baseAmount + taxes.reduce((sum, t) => sum + t.amount, 0);
    return { baseAmount, taxes, totalAmount };
};

// Custom Hook for handling charge codes
const useAppointmentForm = (
    isEdit: boolean,
    selectedDoctorId: string,
    appointmentType: string,
    chargeCodeId: string,
    fetchChargeCodes: (providerId: string, visitType?: string) => Promise<void>,
    chargeCodes: any[],
    setFieldValue: (field: string, value: any) => void
) => {
    useEffect(() => {
        if (selectedDoctorId && appointmentType) {
           
            fetchChargeCodes(selectedDoctorId, appointmentType);
        }
    }, [selectedDoctorId, appointmentType, fetchChargeCodes]);

    useEffect(() => {
        if (isEdit && chargeCodeId && chargeCodes.length > 0) {
            const chargeCodeExists = chargeCodes.find((c) => c.value === chargeCodeId);
            if (chargeCodeExists) {
                setFieldValue("chargeCodeId", chargeCodeId);
            }
        }
    }, [isEdit, chargeCodeId, chargeCodes, setFieldValue]);

    useEffect(() => {
        if (chargeCodeId) {
            const invoice = computeInvoice(chargeCodeId, chargeCodes);
           
        }
    }, [chargeCodeId, chargeCodes]);
};

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
    open,
    onClose,
    initialValues,
    onSave,
    isEdit = false,
    saving = false,
}) => {
    const publisherParticipant = initialValues?.participants?.find(
        (p: any) => p.role === "publisher"
    );

    const subscriberParticipant = initialValues?.participants?.find(
        (p: any) => p.role === "subscriber"
    );

    const mapConsultType = (type?: string) => {
        switch (type) {
            case "virtual":
                return "Tele Consult";
            case "home":
                return "Home Visit";
            case "clinic":
                return "Walk In";
            default:
                return "";
        }
    };

    // Memoize safeInitialValues to prevent recreation on every render
    const safeInitialValues = useMemo(
        () => ({
            date: initialValues?.scheduled_at
                ? moment(initialValues?.scheduled_at).format("YYYY-MM-DD")
                : "",
            time: initialValues?.scheduled_at
                ? moment(initialValues?.scheduled_at).format("HH:mm")
                : "",
            vetId: publisherParticipant?.ref_number?.toString() || "",
            parentId: initialValues?.additional_info?.pet_owner?.user_id?.toString() || "",
            petId: subscriberParticipant?.ref_number?.toString() || "",
            status: initialValues?.consult_status?.slug?.toUpperCase() || "",
            appointmentType: mapConsultType(initialValues?.consult_type),
            reasonForConsult: initialValues?.reason || "",
            chargeCodeId: initialValues?.additional_info?.payment?.charge_code_id?.toString() || "",
        }),
        [initialValues, publisherParticipant, subscriberParticipant]
    );

    const [vets, setVets] = useState<any[]>([]);
    const [loadingVets, setLoadingVets] = useState(false);
    const [parents, setParents] = useState<any[]>([]);
    const [loadingParents, setLoadingParents] = useState(false);
    const [pets, setPets] = useState<any[]>([]);
    const [loadingPets, setLoadingPets] = useState(false);
    const [slots, setSlots] = useState<any[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [chargeCodes, setChargeCodes] = useState<any[]>([]);
    const [loadingChargeCodes, setLoadingChargeCodes] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState("");
    const [selectedDoctorId, setSelectedDoctorId] = useState(safeInitialValues.vetId);
    const [formikValues, setFormikValues] = useState(safeInitialValues);
    const [currentDate, setCurrentDate] = useState(safeInitialValues.date);

    // Use a ref to store Formik's setFieldValue
    const formikSetFieldValueRef = useRef<(field: string, value: any) => void>(() => { });

    // Memoize fetchChargeCodes to prevent recreation
    const fetchChargeCodes = useCallback(async (providerId: string, visitType?: string) => {
        setLoadingChargeCodes(true);
        try {
            const selectedVet = vets.find((v) => v.value === providerId);
            const providerIdToUse = selectedVet?.id || providerId;
            const URL = `v1/resource/chargeCodes?provider_id=${providerIdToUse}&service_type=${visitType || ""}`;
            const response = await apiClient(URL) as any;
            const codes = response?.data?.charge_codes || [];
            if (Array.isArray(codes) && codes.length > 0) {
                const formatted = codes.map((item: any) => ({
                    label: `${item.service_name} - ₹${parseFloat(item.amount || 0).toFixed(2)}`,
                    value: item.id.toString(),
                    code: item.charge_code || "N/A",
                    amount: parseFloat(item.amount || 0) || 0,
                    serviceName: item.service_name || "Unknown Service",
                    tax_codes: item.tax_codes || [],
                }));
                setChargeCodes((prev) => {
                    const isSame = prev.length === formatted.length &&
                        prev.every((item, index) => item.value === formatted[index]?.value && item.label === formatted[index]?.label);
                   
                    return isSame ? prev : formatted;
                });
            } else {
                setChargeCodes([]);
            }
        } catch (error) {
            console.error("Error fetching charge codes:", error);
            setChargeCodes([]);
        } finally {
            setLoadingChargeCodes(false);
        }
    }, [vets]);

    // Call useAppointmentForm at the top level
    useAppointmentForm(
        isEdit,
        selectedDoctorId,
        formikValues.appointmentType,
        formikValues.chargeCodeId,
        fetchChargeCodes,
        chargeCodes,
        (field, value) => formikSetFieldValueRef.current(field, value)
    );

    // Sync currentDate with initial values
    useEffect(() => {
        setCurrentDate(safeInitialValues.date);
    }, [safeInitialValues.date]);

    // Fetch slots when selectedDoctorId or currentDate changes
    useEffect(() => {
        if (selectedDoctorId && currentDate) {
            if (!isEdit || (isEdit && formikValues.status === "RESCHEDULED")) {
                fetchSlots(selectedDoctorId, currentDate);
            } else {
                setSlots([]); // Clear slots if conditions are not met
            }
        } else {
            setSlots([]);
        }
    }, [selectedDoctorId, currentDate, isEdit, formikValues.status]);

    // Fetch initial data
    useEffect(() => {
        fetchVets();
        fetchParents();
    }, []);

    // Fetch pets when parentId changes
    useEffect(() => {
        if (selectedParentId) fetchPets(selectedParentId);
        else setPets([]);
    }, [selectedParentId]);

    // Set parentId and fetch pets in edit mode
    useEffect(() => {
        if (isEdit && safeInitialValues.parentId && parents.length > 0) {
            const parentObj = parents.find((p) => p.value === safeInitialValues.parentId);
            if (parentObj?.id) {
                setSelectedParentId(parentObj.id);
            }
        }
    }, [isEdit, safeInitialValues.parentId, parents]);

    // Set petId in Formik when pets are loaded in edit mode
    useEffect(() => {
        if (isEdit && safeInitialValues.petId && pets.length > 0) {
            const petExists = pets.find((p) => p.value === safeInitialValues.petId);
            if (petExists) {
                // Handled inside Formik
            }
        }
    }, [isEdit, safeInitialValues.petId, pets]);

    // Fetch vets
    const fetchVets = async () => {
        setLoadingVets(true);
        try {
            const params = { limit: "100", page: "1", searchkey: "", order_by: "id", dir: "2", isActiveUsers: "1" };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/providers/all?${queryString}`, { method: "GET" }) as any;
            if (Array.isArray(data.providers)) {
                setVets(
                    data.providers.map((item: any) => ({
                        label: `${item.user.first_name} ${item.user.last_name}`,
                        value: item.user_id.toString(),
                        user_id: item.user_id,
                        id: item.id,
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching vets:", err);
        } finally {
            setLoadingVets(false);
        }
    };

    // Fetch parents
    const fetchParents = async () => {
        setLoadingParents(true);
        try {
            const params = { limit: "100", page: "1", searchkey: "", order_by: "id", dir: "2", isActiveUsers: "1" };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/patients/all?${queryString}`, { method: "GET" }) as any;
            if (Array.isArray(data.patients)) {
                setParents(
                    data.patients.map((item: any) => ({
                        label: `${item.user.first_name} ${item.user.last_name}`,
                        value: item.user_id.toString(),
                        id: item.id,
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching parents:", err);
        } finally {
            setLoadingParents(false);
        }
    };

    // Fetch pets
    const fetchPets = async (parentId: string) => {
        setLoadingPets(true);
        try {
            const params = { primary_id: parentId, limit: "100", page: "1", status: "active" };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/pets/all?${queryString}`, { method: "GET" }) as any;
            if (Array.isArray(data.pets)) {
                setPets(
                    data.pets.map((item: any) => ({
                        label: item?.additional_info?.name,
                        value: item.id.toString(),
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching pets:", err);
        } finally {
            setLoadingPets(false);
        }
    };

    // Fetch slots
    const fetchSlots = async (providerId: string, date: string) => {
        setLoadingSlots(true);
        try {
            const formattedDate = moment(date).format("YYYY-MM-DD");
            const selectedVet = vets.find((v) => v.value === providerId);
            const queryParams = new URLSearchParams({
                provider_id: selectedVet?.id?.toString() || providerId,
                date: formattedDate,
                interval: "30",
            }).toString();

            const response = await apiClient(`v1/users/getSlots?${queryParams}`, { method: "GET" }) as any;
            if (Array.isArray(response.slots)) {
                const now = moment();
                const selectedDateMoment = moment(formattedDate, "YYYY-MM-DD");

                const availableSlots = response.slots
                    .filter((slot: any) => slot.available && !slot.booked)
                    .filter((slot: any) => {
                        const slotDateTime = moment(`${formattedDate} ${slot.start}`, "YYYY-MM-DD HH:mm");
                        if (!slotDateTime.isValid()) return false;
                        const now = moment();
                        const selectedDateMoment = moment(formattedDate, "YYYY-MM-DD");
                        return !selectedDateMoment.isSame(now, "day") || slotDateTime.isAfter(now);
                    })
                    .map((slot: any) => ({
                        value: slot.start,
                        end: slot.end,
                        available: slot.available,
                    }));

                setSlots(availableSlots);

            } else {
                setSlots([]);
            }
        } catch (err) {
            console.error("Error fetching slots:", err);
            setSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const transformPayload = (values: any, isEdit: boolean = false, chargeCodes: any[]) => {
        const selectedVet = vets.find((v) => v.value === values.vetId);
        const selectedParent = parents.find((p) => p.value === values.parentId);
        const selectedPet = pets.find((p) => p.value === values.petId);
        const selectedCharge = chargeCodes.find((c) => c.value === values.chargeCodeId);
        const invoice = computeInvoice(values.chargeCodeId, chargeCodes);
        if (isEdit && values.status !== "RESCHEDULED") {
            // Status-only change payload
            return {
                status: values.status,
                reason: values.reasonForConsult.trim() || "",
            };
        }

        if (isEdit && values.status === "RESCHEDULED") {
            // Reschedule payload
            return {
                provider_id: Number(selectedVet?.value) || 0,
                patient_id: Number(selectedPet?.value) || 0,
                patient_name: selectedPet?.label || "",
                consult_doctor: selectedVet?.label || "Unknown",
                scheduled_at: moment(`${values.date} ${values.time}`).format("YYYY-MM-DD HH:mm:ss"),
                consult_type: values.appointmentType || "",
                reason_for_consult: values.reasonForConsult.trim() || "",
                payment: {
                    amount: parseFloat(invoice?.totalAmount || selectedCharge?.amount || 0),
                },
                status: "RESCHEDULED",
            };
        }

        // Full payload for adding a new appointment
        const taxes = selectedCharge?.tax_codes
            ? JSON.parse(selectedCharge.tax_codes).map((t: any) => ({ ...t, amount: 0 }))
            : [];

        return {
            provider_id: Number(selectedVet?.value) || 0,
            patient_id: Number(selectedParent?.id) || 0,
            patient_name: selectedPet?.label || "",
            consult_doctor: selectedVet?.label || "Unknown",
            consult_date_time: moment(`${values.date} ${values.time}`).format("YYYY-MM-DD HH:mm:ss"),
            consult_type: values.appointmentType || "",
            charge_code_id: Number(values.chargeCodeId) || 0,
            reason_for_consult: values.reasonForConsult.trim() || "",
            payment: selectedCharge
                ? {
                    name: selectedCharge.serviceName || "",
                    code: selectedCharge.code || "",
                    amount: parseFloat(selectedCharge.amount || 0),
                    total_amount: parseFloat(invoice?.totalAmount || selectedCharge.amount || 0),
                    taxes: taxes,
                    tax_total: taxes.reduce((sum: number, t: any) => sum + t.amount, 0),
                    charge_code_id: Number(values.chargeCodeId) || 0,
                }
                : null,
            consult_data: {
                type: values.appointmentType || "",
            },
            additional_info: {
                owner_name: selectedParent?.label || "",
            },
        };
    };

    // Generic text field
    const renderTextField = (
        label: string,
        field: string,
        type: string = "text",
        disabled: boolean = false,
        onChangeCallback?: (value: string) => void
    ) => (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                {label}
            </Typography>
            <Field name={field}>
                {({ field: formikField, meta }: any) => (
                    <TextField
                        {...formikField}
                        type={type}
                        variant="outlined"
                        fullWidth
                        disabled={disabled}
                        onChange={(e) => {
                            formikField.onChange(e);
                            if (onChangeCallback) onChangeCallback(e.target.value as string);
                        }}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        InputLabelProps={type === "date" || type === "time" ? { shrink: true } : {}}
                        sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                background: "#fafafa",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                transition: "0.3s",
                                "&:hover": { boxShadow: disabled ? "none" : "0 4px 10px rgba(0,0,0,0.15)" },
                                "&.Mui-focused": { boxShadow: disabled ? "none" : "0 0 0 3px rgba(25, 118, 210, 0.3)" },
                                "&.Mui-disabled": { background: "#e0e0e0" },
                            },
                        }}
                    />
                )}
            </Field>
        </Box>
    );

    // Generic select field
    const renderSelectField = (
        label: string,
        name: string,
        options: any[],
        loading: boolean,
        disabled: boolean = false,
        onChangeCallback?: (value: string) => void
    ) => (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                {label}
            </Typography>
            <Field name={name}>
                {({ field, form }: any) => (
                    <FormControl
                        fullWidth
                        sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                background: "#fafafa",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                transition: "0.3s",
                                "&:hover": { boxShadow: disabled ? "none" : "0 4px 10px rgba(0,0,0,0.15)" },
                                "&.Mui-focused": { boxShadow: disabled ? "none" : "0 0 0 3px rgba(25, 118, 210, 0.3)" },
                                "&.Mui-disabled": { background: "#e0e0e0" },
                            },
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Select
                                {...field}
                                value={field.value || ""}
                                disabled={disabled}
                                onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                                    if (!disabled) {
                                        form.setFieldValue(name, e.target.value as string);
                                        if (onChangeCallback) onChangeCallback(e.target.value as string);
                                    }
                                }}
                            >
                                {options.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    </FormControl>
                )}
            </Field>
        </Box>
    );

    const formatTo12Hour = (time24: string) => {
        if (!time24 || typeof time24 !== "string") return "";
        const [hourStr, minute] = time24.split(":");
        let hour = parseInt(hourStr, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12;
        return `${hour}:${minute} ${ampm}`;
    };

    // Format slots for select field
    const formattedSlots = slots.map((slot) => ({
        label: `${formatTo12Hour(slot.value)} - ${formatTo12Hour(slot.end || slot.value)}`,
        value: slot.value,
    }));



    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ textAlign: "center" }}>
                {isEdit ? "Edit Appointment" : "Add Appointment"}
            </DialogTitle>

            <Formik
                initialValues={safeInitialValues}
                validationSchema={AppointmentSchema}
                onSubmit={(values) => {
                    const payload = transformPayload(values, isEdit, chargeCodes);
                    onSave(payload);
                }}
                enableReinitialize
            >
                {({ handleSubmit, values, setFieldValue }) => {
                    // Update formikValues and formikSetFieldValueRef without hooks
                    formikSetFieldValueRef.current = setFieldValue;
                    setFormikValues(values);

                    return (
                        <Form onSubmit={handleSubmit}>
                            <DialogContent dividers>
                                {/* Row 1 */}
                                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                                    {renderTextField(
                                        "Date",
                                        "date",
                                        "date",
                                        values.status !== null && values.status !== "" && values.status !== "RESCHEDULED", // field is editable only if status is NOT empty or rescheduled
                                        (value) => {
                                            setCurrentDate(value);
                                            setFieldValue("time", "");
                                        }
                                    )}


                                    {renderSelectField(
                                        "Veterinarian Name",
                                        "vetId",
                                        vets,
                                        loadingVets,
                                        isEdit,
                                        (value) => {
                                            setSelectedDoctorId(value);
                                        }
                                    )}
                                    {renderSelectField(
                                        "Parent Name",
                                        "parentId",
                                        parents,
                                        loadingParents,
                                        isEdit,
                                        (value) => {
                                            const parentObj = parents.find((p) => p.value === value);
                                            const actualParentId = parentObj?.id ?? "";
                                            setSelectedParentId(actualParentId);
                                        }
                                    )}
                                </Box>

                                {/* Row 2 */}
                                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                                    {renderSelectField("Pet Name", "petId", pets, loadingPets, isEdit)}
                                    {renderSelectField(
                                        "Consult Type",
                                        "appointmentType",
                                        appointmentTypes,
                                        false,
                                        isEdit,
                                        (value) => {
                                            if (selectedDoctorId && values.date) fetchSlots(selectedDoctorId, values.date);
                                        }
                                    )}
                                    {isEdit && values.status !== "RESCHEDULED" ? (
                                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                                                Scheduled Time
                                            </Typography>
                                            <TextField
                                                value={values.time}
                                                variant="outlined"
                                                fullWidth
                                                disabled
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    flex: 1,
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "12px",
                                                        background: "#fafafa",
                                                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                                        "&.Mui-disabled": { background: "#e0e0e0" },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        renderSelectField("Available Slots", "time", formattedSlots, loadingSlots, false)
                                    )}


                                </Box>

                                {/* Row 3 */}
                                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                                    {renderSelectField("Charge Code", "chargeCodeId", chargeCodes, loadingChargeCodes, isEdit)}
                                    {renderTextField("Reason for Consult", "reasonForConsult", "text", isEdit)}
                                    {isEdit && (
                                        renderSelectField(
                                            "Status",
                                            "status",
                                            consultStatuses,
                                            false,
                                            false,
                                            (value) => {
                                                if (value === "RESCHEDULED") {
                                                    setFieldValue("time", "");
                                                    if (selectedDoctorId && currentDate) {
                                                        fetchSlots(selectedDoctorId, currentDate);
                                                    }
                                                }
                                            }
                                        )
                                    )}
                                </Box>

                                {/* Invoice Display */}
                                {values.chargeCodeId && (() => {
                                    const invoice = computeInvoice(values.chargeCodeId, chargeCodes);
                                    if (!invoice) return null;

                                    return (
                                        <Box
                                            sx={{
                                                mt: 3,
                                                p: 3,
                                                border: "1px solid #ccc",
                                                borderRadius: 2,
                                                backgroundColor: "#f9f9f9",
                                                maxWidth: 400,
                                                mx: "auto",
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight="bold" mb={2} textAlign="center">
                                                Invoice / Bill
                                            </Typography>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                <Typography>Base Amount</Typography>
                                                <Typography>₹{invoice.baseAmount.toFixed(2)}</Typography>
                                            </Box>
                                            {invoice.taxes.map((tax, idx) => (
                                                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                    <Typography sx={{ fontSize: 14 }}>
                                                        {tax.name} ({tax.type === "percentage" ? `${tax.rate}%` : "Adhoc"})
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 14 }}>₹{tax.amount.toFixed(2)}</Typography>
                                                </Box>
                                            ))}
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                                <Typography fontWeight="bold">Total Amount</Typography>
                                                <Typography fontWeight="bold">₹{invoice.totalAmount.toFixed(2)}</Typography>
                                            </Box>
                                        </Box>
                                    );
                                })()}
                            </DialogContent>

                            <DialogActions sx={{ justifyContent: "center" }}>
                                <Button onClick={onClose} color="secondary">
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="primary">
                                    {saving ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default AppointmentDialog;